import { Box, Icon, IconSize, Popover } from '@bechtel/gds';
import { map } from 'lodash';
import { FC, MutableRefObject, createRef, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { FormMode, MultiLingualText } from '~/components';
import { CommodityInfoTabStyled, TabButtonStyled } from './styled';
import { additionalInfoTabList } from './tab-data';
import { AdditionalInfoTabItem, AdditionalInfoTabType } from './types';

interface AdditionalInfoTabsProps {
  setShowAdditionalInfo: (value: boolean) => void;
  selectedTab: AdditionalInfoTabType;
  setSelectedTab: (tab: AdditionalInfoTabType) => void;
  mode?: string;
  tabs?: AdditionalInfoTabItem[];
}

export const AdditionalInfoTabs: FC<AdditionalInfoTabsProps> = ({
  setShowAdditionalInfo,
  selectedTab,
  setSelectedTab,
  mode,
  tabs,
}) => {
  const elementRef: MutableRefObject<HTMLElement | null>[] = [];
  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const collapseIconRef = useRef(null);
  const { state } = useLocation();
  const [disableTab, setDisableTab] = useState<boolean>(mode === FormMode.CREATE || state?.mode === FormMode.CLONE);
  const theme = useTheme();
  const tabList = tabs && tabs.length > 0 ? tabs : additionalInfoTabList;

  useEffect(() => {
    setDisableTab(mode === FormMode.CREATE || state?.mode === FormMode.CLONE);
  }, [state, mode]);

  return (
    <CommodityInfoTabStyled>
      <Box flexGrow={{ sm: 1 }}>
        {map(tabList, (tabItem: AdditionalInfoTabItem, index: number) => {
          elementRef.push(createRef());
          return (
            <Box
              css={{ background: tabItem.value === selectedTab ? 'white' : 'transparent', position: 'relative' }}
              p={{ sm: '400' }}
              key={tabItem.value}
            >
              <div ref={elementRef[index]}>
                <TabButtonStyled
                  selected={tabItem.value === selectedTab}
                  onClick={() => {
                    if (disableTab) return;
                    setCurrentTab(null);
                    setSelectedTab(tabItem.value);
                  }}
                  onMouseOver={() => !disableTab && setCurrentTab(tabItem.value)}
                  onMouseLeave={() => setCurrentTab(null)}
                  key={tabItem.value}
                >
                  <Icon style={{ opacity: disableTab ? '0.2' : '' }} icon={tabItem.icon} iconStyle="regular" />
                </TabButtonStyled>
              </div>
              <Popover
                triggerComponentRef={elementRef[index]}
                showPopper={currentTab === tabItem.value}
                customStyle={{ backgroundColor: 'black', color: 'white' }}
                placement="bottom"
              >
                <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
                  <MultiLingualText size="300" color="white" family="openSansCondensed">
                    {currentTab}
                  </MultiLingualText>
                </Box>
              </Popover>
            </Box>
          );
        })}
      </Box>
      <div ref={collapseIconRef}>
        <Icon
          icon="arrow-to-right"
          iconStyle="regular"
          size={IconSize.LARGE}
          style={{ width: theme.spacings[500], cursor: 'pointer' }}
          onClick={() => {
            setCurrentTab(null);
            setShowAdditionalInfo(false);
          }}
          onMouseOver={() => setCurrentTab('collapse')}
          onMouseLeave={() => setCurrentTab(null)}
        />
      </div>
      <Popover
        triggerComponentRef={collapseIconRef}
        showPopper={currentTab === 'collapse'}
        customStyle={{ backgroundColor: 'black', color: 'white', maxWidth: '80px' }}
        placement="bottom"
      >
        <Box py={{ sm: '300' }} px={{ sm: '400' }} css={{ textAlign: 'center' }}>
          <MultiLingualText size="300" color="white" family="openSansCondensed">
            Collapse
          </MultiLingualText>
        </Box>
      </Popover>
    </CommodityInfoTabStyled>
  );
};
